import { computed, ref, watch } from 'vue'
import axiosInstance from '@/axios'

/**
 * useArtifiCoverage -- fetch the Artifi-supported color set for a
 * supplier SKU so the editor can dim swatches Artifi has no preview
 * support for and swap the technical sku_not_configured error message
 * for a product-state message.
 *
 * Backed by GET /api/artifi/supported_color_variants. Module-level
 * cache so multiple components watching the same SKU produce a single
 * request and a shared result. The backend cache TTL is 24h
 * (TTL_PRODUCT_ATTRIBUTES_S) -- the module-level memo is per-page-load,
 * which is more than enough for the editor lifetime.
 *
 * Public surface:
 *
 *   const { supported, supportedSet, loading, error, isUnsupported,
 *       coverageFootnote } = useArtifiCoverage(supplierSkuRef)
 *
 *   supplierSkuRef:   Ref<string|null>             // null -> no fetch
 *   supported:        Ref<string[]>                // ordered, raw response
 *   supportedSet:     Ref<Set<string>>             // O(1) membership, RAW list
 *   loading:          Ref<boolean>                 // request in flight
 *   error:            Ref<{code,message}|null>     // any fetch error
 *   notFound:         Ref<boolean>                 // true iff 404 sku_not_found
 *   styleUnsupported: ComputedRef<boolean>         // Artifi can render NO color
 *   isUnsupported:    (colorName: string) => bool  // false on loading/error
 *   coverageFootnote: (colorName: string) =>
 *       {allFail: bool, failingViews: string[]} | null
 *
 * TWO DERIVATIONS, ON PURPOSE (#2060). Two different response fields feed
 * two different questions. This is deliberate, not drift:
 *
 *   supportedSet      <- data.supported (the raw color list). PUBLIC. The
 *                        "what colors did Artifi answer with" view. Consumers
 *                        use its size as a "did coverage load at all" gate.
 *   supportedNormSet  <- Object.keys(data.supported_with_variant_sku), i.e.
 *                        the RESOLVED variant map. INTERNAL (deliberately not
 *                        returned) -- reachable only via isUnsupported.
 *                        The "can Artifi preview THIS color" view.
 *
 * Per #2057 the dim/hide predicate MUST key off the resolved map: the backend
 * keeps residual Class B colors (no resolvable variant SKU) in ``supported``
 * but omits them from the map, so a predicate built on the raw list would
 * leave exactly those colors wrongly un-dimmed. Keeping supportedNormSet off
 * the public surface is what stops a consumer from re-deriving the predicate
 * from the wrong field. The two sets are therefore NOT interchangeable and
 * NOT expected to have equal size -- see the ``coverageReady`` gate in
 * ColorsPanel.vue / SupplierSkuPanel.vue for the consumer-side note.
 *
 * ``notFound`` (issue #2202) distinguishes the EXPECTED "Artifi does not
 * recognize this SKU" state (404 ``sku_not_found``) from a genuine
 * failure, so the UI can show a friendly message instead of an error
 * banner. It fails open the same way as any other error (no dimming).
 *
 * isUnsupported intentionally returns false when the coverage list is
 * loading or has errored. The editor's swatch render fails open: we
 * never dim a swatch we cannot prove is unsupported, because dimming
 * a real color is more misleading than not dimming an uncovered one.
 * The Stage's structured-404 fallback still catches the click after the
 * fact -- this composable just lets us surface the gap proactively
 * when we know about it.
 *
 * ``styleUnsupported`` (card #243, GH #4443) answers the STYLE-level
 * question "can Artifi draw this style at all": true when Artifi does not
 * know the SKU (``notFound``) or the backend states ``style_renderable:
 * false`` (it knows color names but NO color resolves to a renderable
 * variant -- PC380, ST350LS). Without it that second case was
 * indistinguishable from "coverage not loaded" and the editor offered no
 * fallback. It is false while loading, on any other error, and when the
 * backend could not tell (``style_renderable: null`` or absent) -- the same
 * fail-open rule as isUnsupported. It never dims or hides anything; it only
 * switches the preview surface to its fallbacks.
 *
 * ``coverageFootnote`` (unrenderable-colors blueprint 5.1, Phase 5) is
 * THE single determination-rule source for the footnote UI across all
 * three surfaces (ColorsPanel grid, ColorFilmstrip strip, Stage canvas).
 * It reads the backend's per-(variant SKU, view) verdict map
 * (``view_verdicts``, keyed by variant SKU, NOT color name) and answers
 * "does this color have a footnote, and if so is it a total or partial
 * failure". No consumer re-implements this logic -- they all call this
 * function. Returns null when there is no verdict data yet (not probed,
 * fully supported, or the color has no resolvable variant SKU) -- the
 * safe fail-open default (never invent a footnote from absent data).
 */
const ENDPOINT = '/api/artifi/supported_color_variants'

/**
 * Normalize a color name to a comparison key: casefold + drop every
 * non-alphanumeric character (spaces, slashes, punctuation). Artifi's
 * coverage payload keys colors inconsistently -- "Charcoal" (spaced style)
 * next to "CoalGrey" (un-spaced) in the SAME response -- while the product
 * stores the spaced form ("Coal Grey"). Comparing raw strings reported a
 * supported color as unsupported and drew a false "no Artifi preview" eye.
 * Normalizing BOTH sides folds "Coal Grey" and "CoalGrey" to "coalgrey".
 *
 * Mirrors Stage.vue's _normalizeColor and the backend
 * resolver._normalize_color_name so all layers agree on the equivalence
 * class (also collapses SanMar slash colors "Black/ Red" vs "Black/Red").
 *
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function normalizeColorKey(name) {
    if (typeof name !== 'string') return ''
    let out = ''
    for (const ch of name.toLowerCase()) {
        if (/[a-z0-9]/.test(ch)) out += ch
    }
    return out
}

// Module-level cache. Key: supplier_sku. Value: { supported, supportedSet, error }.
// Promises are tracked separately so two callers watching the same SKU
// share one axios request.
const _cache = new Map()
const _inflight = new Map()

function _fetch(supplierSku) {
    if (_cache.has(supplierSku)) {
        return Promise.resolve(_cache.get(supplierSku))
    }
    if (_inflight.has(supplierSku)) {
        return _inflight.get(supplierSku)
    }
    const promise = axiosInstance
        .get(ENDPOINT, { params: { supplier_sku: supplierSku } })
        .then((resp) => {
            const data = resp.data || {}
            const supported = Array.isArray(data.supported) ? data.supported : []
            const supportedWithVariantSku =
                data.supported_with_variant_sku || {}
            // Phase 3/5 (blueprint 4.2/5.1): per-(variant SKU, view) verdict
            // map, keyed by variant SKU (NOT color name) -- always present
            // in the real response, may be {} when the SKU's views could
            // not be determined. Defensive default here so an older/stubbed
            // response shape (e.g. existing test fixtures that predate this
            // field) still resolves to an empty map rather than throwing.
            const viewVerdicts = data.view_verdicts || {}
            // #243 / GH #4443: true | false | null (null = the backend could
            // not tell). Anything but a boolean is treated as "unknown".
            const styleRenderable = typeof data.style_renderable === 'boolean'
                ? data.style_renderable
                : null
            const result = {
                styleRenderable,
                supported,
                supportedSet: new Set(supported),
                // Normalized membership set used by isUnsupported. It is built
                // from the KEYS of the RESOLVED VARIANT MAP, not the raw
                // 'supported' color list -- a color is "supported for preview"
                // IFF it has a resolvable Artifi variant SKU. The backend keeps
                // residual Class B colors (no resolvable SKU) in 'supported'
                // (the full ColorAttribute set) but omits them from the map, so
                // keying the predicate off 'supported' would leave those
                // residual colors wrongly un-dimmed. Keying off the map dims
                // exactly the colors Artifi cannot preview. normalizeColorKey
                // folds the spaced-vs-unspaced mismatch (Coal Grey vs CoalGrey)
                // so the membership check still matches the product's label.
                // The raw supportedSet above is left untouched as the public
                // color-list view for any consumer that wants it.
                supportedNormSet: new Set(
                    Object.keys(supportedWithVariantSku).map(normalizeColorKey),
                ),
                supportedWithVariantSku,
                viewVerdicts,
                error: null,
            }
            // An explicit unknown (null) means the backend could not build the
            // variant map this time (transient Artifi error) and did not cache
            // it either: do not remember it here, so the next mount retries.
            // A definite true/false, or a response that predates the field
            // (undefined), is cached as before.
            if (data.style_renderable !== null) _cache.set(supplierSku, result)
            return result
        })
        .catch((err) => {
            const errPayload =
                err?.response?.data?.error || {
                    code: 'network_error',
                    message: err?.message || 'Network error',
                }
            // We intentionally do NOT cache errors -- a transient
            // failure should retry on the next mount, and the "fail
            // open" semantics in isUnsupported mean an uncached error
            // is harmless (no dimming).
            return {
                supported: [],
                supportedSet: new Set(),
                supportedNormSet: new Set(),
                supportedWithVariantSku: {},
                viewVerdicts: {},
                styleRenderable: null,
                error: errPayload,
            }
        })
        .finally(() => {
            _inflight.delete(supplierSku)
        })
    _inflight.set(supplierSku, promise)
    return promise
}

/**
 * THE renderability predicate, as a pure function over one fetch result.
 * True only when coverage is KNOWN: a result with an error, or with an empty
 * resolved variant map (no data, or a style Artifi cannot draw at all), never
 * marks a color unsupported -- we do not dim or skip what we cannot prove is
 * unrenderable. Used by the composable below AND by awaitArtifiCoverage, so
 * the swatch grids and the default-color pick cannot disagree.
 *
 * @param {{error?: object|null, supportedNormSet?: Set<string>}|null} result
 * @param {string} colorName
 */
function isUnsupportedIn(result, colorName) {
    if (!result || result.error) return false
    const set = result.supportedNormSet
    if (!set || set.size === 0) return false
    return !set.has(normalizeColorKey(colorName))
}

/**
 * One-shot, component-free read of coverage for callers that decide ONCE and
 * must not act on a half-loaded answer (the new-product default color, the
 * confirmed-swap default color). Resolves when the shared fetch lands, or
 * after `timeoutMs`, whichever is first; it never rejects.
 *
 * `settled` is false when the wait timed out. Either way `isUnsupported`
 * answers with the composable's rule, so an unsettled/errored/empty result
 * marks nothing unrenderable (fail open): the caller then picks among colors
 * not KNOWN to be unrenderable, exactly as before coverage existed.
 *
 * @param {string|null|undefined} supplierSku  null/blank: nothing to fetch
 * @param {{timeoutMs?: number}} [opts]
 * @returns {Promise<{settled: boolean, styleUnsupported: boolean,
 *   isUnsupported: (colorName: string) => boolean}>}
 */
export async function awaitArtifiCoverage(supplierSku, { timeoutMs = 6000 } = {}) {
    const open = (settled) => ({
        settled,
        styleUnsupported: false,
        isUnsupported: () => false,
    })
    if (!supplierSku || typeof supplierSku !== 'string') return open(true)
    let timer
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs)
    })
    const result = await Promise.race([_fetch(supplierSku), timeout])
    clearTimeout(timer)
    if (!result) return open(false)
    return {
        settled: true,
        styleUnsupported: !result.error && result.styleRenderable === false,
        isUnsupported: (name) => isUnsupportedIn(result, name),
    }
}

/**
 * Test-only: clear the module-level cache. Exposed so component specs
 * can re-isolate per-test without leaking results between cases.
 */
export function _resetArtifiCoverageCache() {
    _cache.clear()
    _inflight.clear()
}

export function useArtifiCoverage(supplierSkuRef) {
    const supported = ref([])
    const supportedSet = ref(new Set())
    const supportedNormSet = ref(new Set())
    const supportedWithVariantSku = ref({})
    // view_verdicts response field (blueprint 4.2/5.1), keyed by variant
    // SKU: { [variantSku]: { [viewCode]: true|false|null } }.
    const viewVerdicts = ref({})
    const loading = ref(false)
    const error = ref(null)
    const notFound = ref(false)
    const styleRenderable = ref(null)

    const styleUnsupported = computed(() => {
        if (loading.value) return false
        if (notFound.value) return true
        if (error.value) return false
        return styleRenderable.value === false
    })

    function isUnsupported(colorName) {
        if (loading.value || error.value) return false
        return isUnsupportedIn(
            { error: error.value, supportedNormSet: supportedNormSet.value },
            colorName,
        )
    }

    /**
     * THE single determination-rule function for the footnote UI
     * (blueprint 5.1, Phase 5). Resolves colorName -> variant SKU via
     * supportedWithVariantSku (the SAME map isUnsupported/the coverage
     * hide-filter already key off), then looks up that SKU's per-view
     * verdicts. Returns:
     *
     *   null                                  -- no footnote (no data yet,
     *                                             unresolved SKU, or every
     *                                             known view passed)
     *   { allFail: true,  failingViews: [...] } -- every KNOWN view failed
     *                                             AND every configured view
     *                                             has been probed (no
     *                                             unprobed gap masking a
     *                                             real pass)
     *   { allFail: false, failingViews: [...] } -- some views failed, at
     *                                             least one passed or is
     *                                             still unprobed
     *
     * Fail-open: absent/empty verdict data returns null, never a fabricated
     * failure. ColorsPanel / ColorFilmstrip / Stage all call this function
     * directly -- none re-implements the allFail/partial logic.
     *
     * @param {string} colorName
     * @returns {{allFail: boolean, failingViews: string[]} | null}
     */
    function coverageFootnote(colorName) {
        const map = supportedWithVariantSku.value || {}
        let variantSku = null
        if (Object.prototype.hasOwnProperty.call(map, colorName)) {
            variantSku = map[colorName]
        } else {
            // Same normalized-key fallback isUnsupported/Stage's client-side
            // resolver use (Coal Grey vs CoalGrey, slash-spacing, casing).
            const queryNorm = normalizeColorKey(colorName)
            if (queryNorm) {
                for (const [k, v] of Object.entries(map)) {
                    if (normalizeColorKey(k) === queryNorm) {
                        variantSku = v
                        break
                    }
                }
            }
        }
        if (!variantSku) return null
        const verdicts = viewVerdicts.value?.[variantSku]
        if (!verdicts) return null
        const entries = Object.entries(verdicts)
        const known = entries.filter(([, v]) => v !== null)
        if (known.length === 0) return null
        const failing = known.filter(([, v]) => v === false).map(([view]) => view)
        if (failing.length === 0) return null
        const allFail = failing.length === known.length
            && known.length >= entries.length
        return { allFail, failingViews: failing }
    }

    watch(
        () => (typeof supplierSkuRef === 'function'
            ? supplierSkuRef()
            : supplierSkuRef?.value ?? supplierSkuRef),
        async (sku) => {
            if (!sku || typeof sku !== 'string') {
                supported.value = []
                supportedSet.value = new Set()
                supportedNormSet.value = new Set()
                supportedWithVariantSku.value = {}
                viewVerdicts.value = {}
                styleRenderable.value = null
                error.value = null
                notFound.value = false
                loading.value = false
                return
            }
            loading.value = true
            error.value = null
            notFound.value = false
            const result = await _fetch(sku)
            // Only commit if the SKU is still the one we asked for --
            // a fast SKU switch must not clobber the wrapper with a
            // stale prior response.
            const current = typeof supplierSkuRef === 'function'
                ? supplierSkuRef()
                : supplierSkuRef?.value ?? supplierSkuRef
            if (current !== sku) return
            supported.value = result.supported
            supportedSet.value = result.supportedSet
            supportedNormSet.value = result.supportedNormSet
            supportedWithVariantSku.value = result.supportedWithVariantSku
            viewVerdicts.value = result.viewVerdicts || {}
            styleRenderable.value = result.styleRenderable ?? null
            error.value = result.error
            // Issue #2202: 404 sku_not_found is the expected "unrecognized
            // SKU" state, not a fault -- flag it for a friendly message.
            notFound.value = result.error?.code === 'sku_not_found'
            loading.value = false
        },
        { immediate: true },
    )

    return {
        supported,
        supportedSet,
        supportedWithVariantSku,
        loading,
        error,
        notFound,
        styleUnsupported,
        isUnsupported,
        coverageFootnote,
    }
}

export default useArtifiCoverage
